import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageCircle, Send } from 'lucide-react'
import ProfileLink from '../../../components/ProfileLink'
import { formatAgo } from '../../profile/utils/profileFormat'
import { personName, roleLabel } from '../utils/teamFormat'
import { accentColor, cardPrimaryColor, contentBgColor, roleBadgeStyle } from '../utils/teamColors'
import { CARD_CLASS } from '../utils/teamUi'
import { teamApi } from '../services/teamApi'

const POLL_DELAY = 3000
const RETRY_DELAY = 10000
const MAX_MESSAGES = 100

const messageLength = (text) => Array.from(text).length
const newClientId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('')

const mergeMessages = (current, incoming, reset = false) => {
  const byId = new Map((reset ? [] : current).map((message) => [message.id, message]))
  incoming.forEach((message) => byId.set(message.id, message))
  return [...byId.values()].sort((a, b) => a.id - b.id).slice(-MAX_MESSAGES)
}

const TeamChat = ({ team }) => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const [canWrite, setCanWrite] = useState(false)
  const [maxLength, setMaxLength] = useState(500)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [retryAfter, setRetryAfter] = useState(null)
  const scrollRef = useRef(null)
  const stickToBottom = useRef(true)
  const pollNow = useRef(null)
  const stopPolling = useRef(null)
  const draft = useRef(null)
  const sendingRef = useRef(false)
  const active = useRef(true)

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element && stickToBottom.current) element.scrollTop = element.scrollHeight
  }, [messages])

  useEffect(() => {
    if (retryAfter == null) return undefined
    if (retryAfter === 0) {
      setRetryAfter(null)
      return undefined
    }
    const timer = setTimeout(() => setRetryAfter((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [retryAfter])

  useEffect(() => {
    active.current = true
    let timer = null
    let controller = null
    let busy = false
    let pending = false
    let stopped = false
    let initialized = false
    let cursor = null

    const clearTimer = () => {
      if (timer) clearTimeout(timer)
      timer = null
    }
    const stop = () => {
      stopped = true
      clearTimer()
      controller?.abort()
    }
    const poll = async () => {
      if (stopped || document.visibilityState !== 'visible') return
      if (busy) {
        pending = true
        return
      }
      busy = true
      controller = new AbortController()
      const result = await teamApi.getChat(initialized ? cursor : null, controller.signal)
      controller = null
      busy = false
      if (stopped || document.visibilityState !== 'visible') return
      if (result.aborted) {
        if (pending) {
          pending = false
          poll()
        }
        return
      }
      if ((result.status === 403 && result.data?.error === 'not_in_team') ||
          (result.status === 503 && ['chat_disabled', 'mode_disabled'].includes(result.data?.error))) {
        stop()
        setUnavailable(true)
        return
      }
      let delay = POLL_DELAY
      if (result.ok && Array.isArray(result.data?.messages) && result.data.last_id != null) {
        if (result.data.reset === true || result.data.messages.length > 0) {
          const element = scrollRef.current
          stickToBottom.current = !element || element.scrollHeight - element.scrollTop - element.clientHeight < 60
          setMessages((current) => mergeMessages(current, result.data.messages, result.data.reset === true))
        }
        cursor = result.data.last_id
        initialized = true
        setCanWrite(result.data.can_write === true)
        if (Number.isInteger(result.data.max_length) && result.data.max_length > 0) setMaxLength(result.data.max_length)
        setLoaded(true)
        setLoadError(false)
      } else {
        if (!initialized) setLoadError(true)
        delay = RETRY_DELAY
      }
      if (pending && delay === POLL_DELAY) {
        pending = false
        poll()
      } else {
        pending = false
        timer = setTimeout(poll, delay)
      }
    }
    const onVisibilityChange = () => {
      clearTimer()
      if (document.visibilityState !== 'visible') controller?.abort()
      else poll()
    }
    pollNow.current = () => {
      clearTimer()
      poll()
    }
    stopPolling.current = stop
    document.addEventListener('visibilitychange', onVisibilityChange)
    poll()
    return () => {
      active.current = false
      stop()
      pollNow.current = null
      stopPolling.current = null
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [team.id])

  const handleTextChange = (event) => {
    setText(event.target.value)
    setSendError(null)
    if (draft.current?.text !== event.target.value) draft.current = null
  }

  const handleSend = async (event) => {
    event.preventDefault()
    if (sendingRef.current || retryAfter > 0 || !canWrite || !loaded || !text.trim()) return
    if (messageLength(text) > maxLength) {
      setSendError('text_too_long')
      return
    }
    if (text.split('\n').length > 10) {
      setSendError('too_many_lines')
      return
    }
    if (!draft.current || draft.current.text !== text) {
      draft.current = { text, id: newClientId() }
    }
    const sent = draft.current
    sendingRef.current = true
    setSending(true)
    setSendError(null)
    setRetryAfter(null)
    const { ok, status, data } = await teamApi.sendChat(sent.text, sent.id)
    sendingRef.current = false
    if (!active.current) return
    setSending(false)
    if (ok && data?.message) {
      stickToBottom.current = true
      setMessages((current) => mergeMessages(current, [data.message]))
      if (draft.current === sent) {
        draft.current = null
        setText('')
      }
      pollNow.current?.()
      return
    }
    const error = data?.error
    if (status === 403 && error === 'not_in_team' || status === 503 && ['chat_disabled', 'mode_disabled'].includes(error)) {
      stopPolling.current?.()
      setUnavailable(true)
      return
    }
    if (status === 409 && ['team_frozen', 'team_deleting'].includes(error)) setCanWrite(false)
    setSendError(error || 'send_failed')
    if (status === 429 && Number.isFinite(data?.retry_after)) setRetryAfter(Math.max(0, Math.ceil(data.retry_after)))
  }

  if (unavailable) return null

  const primary = cardPrimaryColor(team.colors)
  const accent = accentColor(team.colors)
  const subtleBorder = `${primary}1F`
  const length = messageLength(text)
  const errorCodes = new Set([
    'empty_text', 'text_too_long', 'too_many_lines', 'flood_text', 'link_forbidden',
    'invalid_text', 'invalid_client_id', 'team_frozen', 'team_deleting', 'duplicate',
    'too_fast', 'rate_limited', 'team_rate_limited'
  ])
  const errorKey = errorCodes.has(sendError) ? sendError : 'send_failed'

  return (
    <section className={`${CARD_CLASS} p-4`} style={{ backgroundColor: contentBgColor(team.colors), borderColor: subtleBorder }}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: accent }}>
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}1A` }}>
          <MessageCircle size={14} />
        </span>
        {t('team.chat.title')}
      </h2>
      <div
        ref={scrollRef}
        onScroll={(event) => {
          const element = event.currentTarget
          stickToBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 60
        }}
        role="log"
        aria-label={t('team.chat.title')}
        aria-live="polite"
        className="h-80 space-y-3 overflow-y-auto rounded-xl border p-3"
        style={{ borderColor: subtleBorder, backgroundColor: `${primary}0D` }}
      >
        {!loaded && <p className="py-12 text-center text-sm" style={{ color: primary }}>{loadError ? t('team.chat.loadError') : t('team.chat.loading')}</p>}
        {loaded && messages.length === 0 && <p className="py-12 text-center text-sm" style={{ color: primary }}>{t('team.chat.empty')}</p>}
        {messages.map((message) => {
          const author = message.author
          const name = personName(author, t, t('team.chat.unknownAuthor'))
          return (
            <div key={message.id} className={`flex gap-2 ${message.mine ? 'flex-row-reverse' : ''}`}>
              <ProfileLink slug={author?.slug} className="shrink-0 self-end">
                <span className="block h-8 w-8 overflow-hidden rounded-full border" style={{ borderColor: subtleBorder, backgroundColor: `${primary}1A` }}>
                  {author?.ava && <img src={author.ava} alt={name} className="h-full w-full object-cover" />}
                </span>
              </ProfileLink>
              <div className={`min-w-0 max-w-[85%] rounded-xl border px-3 py-2 ${message.mine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                style={{ backgroundColor: message.mine ? `${accent}24` : `${primary}0D`, borderColor: message.mine ? `${accent}55` : subtleBorder }}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <ProfileLink slug={author?.slug} className="min-w-0 max-w-full truncate text-xs font-semibold" style={{ color: primary }}>{name}</ProfileLink>
                  {author?.role && <span className="rounded-full border px-1.5 text-[10px]" style={roleBadgeStyle(author.role, team.colors)}>{roleLabel(author.role, t)}</span>}
                  <span className="text-[10px] opacity-70" style={{ color: primary }}>{formatAgo(message.sec_ago, t)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]" style={{ color: primary }}>{message.text}</p>
              </div>
            </div>
          )
        })}
      </div>
      {loadError && !loaded && (
        <button type="button" onClick={() => pollNow.current?.()} className="mt-2 text-xs font-semibold active:scale-[0.98]" style={{ color: primary }}>
          {t('team.chat.retry')}
        </button>
      )}
      {loaded && !canWrite && <p className="mt-3 text-sm" style={{ color: primary }}>{t('team.chat.readOnly')}</p>}
      {loaded && canWrite && (
        <form onSubmit={handleSend} className="mt-3">
          <label htmlFor="team-chat-text" className="sr-only">{t('team.chat.placeholder')}</label>
          <textarea
            id="team-chat-text"
            value={text}
            onChange={handleTextChange}
            rows={2}
            placeholder={t('team.chat.placeholder')}
            className="w-full resize-none rounded-xl border bg-transparent px-3 py-2 text-sm outline-none placeholder:opacity-60 focus:ring-1"
            style={{ borderColor: subtleBorder, color: primary, '--tw-ring-color': accent }}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-xs tabular-nums" style={{ color: length > maxLength ? 'var(--error)' : primary }}>{length}/{maxLength}</span>
            <button type="submit" disabled={sending || retryAfter > 0 || !text.trim() || length > maxLength}
              className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-dark-primary transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: accent }}>
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {t('team.chat.send')}
            </button>
          </div>
        </form>
      )}
      {sendError && <p role="alert" className="mt-2 text-xs text-error">
        {t(`team.chat.errors.${errorKey}`)}
      </p>}
      {retryAfter > 0 && <p className="mt-1 text-xs" style={{ color: primary }}>{t('team.chat.retryAfter', { count: retryAfter })}</p>}
    </section>
  )
}

export default TeamChat
